import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  UploadCloud,
  FileText,
  Loader2,
  Headphones,
  Gauge,
  X,
  Sparkles,
  Mic,
  Check,
  Smartphone,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import QuotaBar from "@/components/audioflip/QuotaBar";
import VoiceCloneModal from "@/components/audioflip/VoiceCloneModal";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICE_IDS = ["onyx", "nova", "echo", "shimmer", "alloy", "fable", "sage"];
const VOICE_LANG_TAG = {
  onyx: "ES",
  nova: "ES",
  echo: "ES",
  shimmer: "ES",
  alloy: "EN",
  fable: "EN",
  sage: "EN",
};

// Curated native Spanish voices from HeyGen. voice_id values are stable
// across HeyGen accounts. These sound like native Latino/Spanish speakers,
// unlike the OpenAI voices which have a light American accent when
// speaking Spanish.
const HEYGEN_NATIVE_VOICES = [
  { id: "626ca51acb2e496f8dcee8d7591fda3c", name: "Mateo Narrador", region: "LATAM", gender: "M" },
  { id: "747e5085c2ed4546bd79934c440d8854", name: "Diego Martínez", region: "LATAM", gender: "M" },
  { id: "3daec88b3c1a49b7a5e10a211426fc81", name: "Fernando Sanz", region: "ES", gender: "M" },
  { id: "2f448ec9e69b48b0a117bd1334d7e5ad", name: "Carlos Claro", region: "LATAM", gender: "M" },
  { id: "6ce26db0cb6f4e7881b85452619f7f19", name: "Camila Vega", region: "LATAM", gender: "F" },
  { id: "689f48196a9a43c4bbbb67c14fdbb4c6", name: "Sara Martín", region: "ES", gender: "F" },
  { id: "4621efe09dea43a38a2e1e6682b0b768", name: "Sofía Alegre", region: "LATAM", gender: "F" },
];
const HEYGEN_NATIVE_IDS = new Set(HEYGEN_NATIVE_VOICES.map((v) => v.id));

const MAX_BYTES = 50 * 1024 * 1024;
const MAX_CHARS = 500000;
const AXIOS_TIMEOUT_MS = 30 * 60 * 1000;

export default function Hero({ onAudioReady }) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [voice, setVoice] = useState("626ca51acb2e496f8dcee8d7591fda3c");
  const [speed, setSpeed] = useState(1);
  const [extracting, setExtracting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [quotaRefresh, setQuotaRefresh] = useState(0);
  const [hdMode, setHdMode] = useState(false);
  const [myVoices, setMyVoices] = useState([]);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const lastNormalizedRef = useRef("");

  // Points to whatever public URL we're being served from (works for both
  // the preview environment and the deployed prod URL like audioflip.org).
  const qrUrl = useMemo(() => {
    if (typeof window === "undefined") return "https://audioflip.org";
    return window.location.origin;
  }, []);

  const normalizeNow = useCallback(async (silent = false) => {
    if (!text || !text.trim()) return;
    setNormalizing(true);
    try {
      const { data } = await axios.post(
        `${API}/normalize-text`,
        { text },
        { timeout: 15000 }
      );
      const fixed = data?.text;
      if (fixed && fixed !== text) {
        setText(fixed);
        lastNormalizedRef.current = fixed;
        if (!silent) {
          toast.success("Texto normalizado", {
            description: "Palabras cortadas restauradas (fi/fl).",
          });
        }
      } else {
        lastNormalizedRef.current = text;
      }
    } catch {
      /* silent — best-effort */
    } finally {
      setNormalizing(false);
    }
  }, [text]);

  // Auto-normalize with debounce when text changes via paste/typing. Skips
  // if we've already normalized this exact string (no infinite loop).
  useEffect(() => {
    if (!text.trim()) return;
    if (text === lastNormalizedRef.current) return;
    // Only auto-fire when the text is meaningfully long — skip single chars
    // while user is typing manually.
    if (text.length < 20) return;
    const id = setTimeout(() => {
      normalizeNow(true);
    }, 800);
    return () => clearTimeout(id);
  }, [text, normalizeNow]);

  const refreshMyVoices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/voices/mine`);
      setMyVoices(data.voices || []);
    } catch {
      /* ignore — endpoint may 400 during SSR without session */
    }
  }, []);

  useEffect(() => {
    refreshMyVoices();
  }, [refreshMyVoices]);

  // Poll while any voice is still processing.
  useEffect(() => {
    const hasProcessing = myVoices.some(
      (v) => v.status !== "ready" && v.status !== "failed"
    );
    if (!hasProcessing) return;
    const id = setInterval(refreshMyVoices, 8000);
    return () => clearInterval(id);
  }, [myVoices, refreshMyVoices]);

  const isHeygenVoice =
    myVoices.some((v) => v.voice_id === voice) || HEYGEN_NATIVE_IDS.has(voice);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(t("toasts.fileTooLarge"), {
        description: t("toasts.fileTooLargeDesc"),
      });
      return;
    }
    const okExt = /\.(pdf|docx|txt)$/i.test(file.name);
    if (!okExt) {
      toast.error(t("toasts.badFormat"), {
        description: t("toasts.badFormatDesc"),
      });
      return;
    }
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/extract`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText(data.text || "");
      setFilename(data.filename || file.name);
      toast.success(t("toasts.extracted"), {
        description: t("toasts.extractedDesc", {
          count: data.char_count.toLocaleString(),
        }),
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || t("toasts.extractFailedDesc");
      toast.error(t("toasts.extractFailed"), { description: detail });
    } finally {
      setExtracting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const handleGenerate = async () => {
    const clean = text.trim();
    if (!clean) {
      toast.error(t("toasts.noText"), { description: t("toasts.noTextDesc") });
      return;
    }
    if (clean.length > MAX_CHARS) {
      toast.error(t("toasts.textTooLong"), {
        description: t("toasts.textTooLongDesc", {
          max: MAX_CHARS.toLocaleString(),
        }),
      });
      return;
    }
    setGenerating(true);
    setProgress({ done: 0, total: 0 });
    const chunksCount = Math.ceil(clean.length / 4000);
    if (chunksCount > 3) {
      toast.info(t("toasts.longJob"), {
        description: t("toasts.longJobDesc", { n: chunksCount }),
      });
    }
    try {
      const start = await axios.post(
        `${API}/generate-audio`,
        {
          text: clean,
          voice,
          speed,
          model: hdMode ? "tts-1-hd" : "tts-1",
          provider: isHeygenVoice ? "heygen" : "openai",
        },
        { timeout: 30000 }
      );
      const jobId = start.data.job_id;
      const total = start.data.total_chunks;
      setProgress({ done: 0, total });

      const deadline = Date.now() + AXIOS_TIMEOUT_MS;
      let status = "pending";
      let lastErr = null;
      while (status !== "done") {
        if (Date.now() > deadline) throw new Error(t("toasts.timeoutError"));
        await new Promise((r) => setTimeout(r, 2500));
        const s = await axios.get(`${API}/jobs/${jobId}`, { timeout: 20000 });
        status = s.data.status;
        setProgress({ done: s.data.done_chunks, total: s.data.total_chunks });
        if (status === "error") {
          lastErr = s.data.error || t("toasts.genFailedDesc");
          break;
        }
      }
      if (status === "error") throw new Error(lastErr);

      const res = await axios.get(`${API}/jobs/${jobId}/download`, {
        responseType: "blob",
        timeout: 5 * 60 * 1000,
      });
      const blob = new Blob([res.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      onAudioReady(url, {
        filename: (filename || "audioflip").replace(/\.[^.]+$/, "") + ".mp3",
        voice,
        speed,
        chars: clean.length,
      });
      toast.success(t("toasts.audioReady"), {
        description: t("toasts.audioReadyDesc"),
      });
    } catch (err) {
      let detail = err?.message || t("toasts.genFailedDesc");
      const respData = err?.response?.data;
      if (respData) {
        try {
          if (respData instanceof Blob) {
            const txt = await respData.text();
            detail = JSON.parse(txt).detail || detail;
          } else if (typeof respData === "object" && respData.detail) {
            detail = respData.detail;
          }
        } catch {
          /* ignore */
        }
      }
      toast.error(t("toasts.genFailed"), { description: detail });
    } finally {
      setGenerating(false);
      setProgress({ done: 0, total: 0 });
      setQuotaRefresh((n) => n + 1);
    }
  };

  const clearText = () => {
    setText("");
    setFilename("");
  };

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#EFF4FF] blur-3xl opacity-70" />
        <div className="absolute top-40 -left-32 h-72 w-72 rounded-full bg-[#F4F4F6] blur-3xl opacity-60" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-24 sm:pt-20 lg:px-10 lg:pt-24">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-2 w-2 rounded-full bg-[#002FA7]" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
            {t("hero.kicker")}
          </span>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h1
              data-testid="hero-title"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-zinc-950"
            >
              {t("hero.title_part1")}
              <span className="italic text-[#002FA7]">{t("hero.title_emphasis")}</span>
              {t("hero.title_part2")}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-6 max-w-2xl font-sans text-lg leading-relaxed text-zinc-600"
            >
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                <Sparkles className="h-3.5 w-3.5 text-[#002FA7]" />
                {t("hero.pill_neural")}
              </div>
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                <FileText className="h-3.5 w-3.5 text-[#002FA7]" />
                {t("hero.pill_formats")}
              </div>
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                <Gauge className="h-3.5 w-3.5 text-[#002FA7]" />
                {t("hero.pill_speed")}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col items-stretch justify-center gap-4">
            {/* QR code so visitors can open AudioFlip on their phone / tablet.
                Points to the current origin so preview and prod both work. */}
            <div
              data-testid="hero-qr-widget"
              className="hidden sm:flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white/60 backdrop-blur p-4 shadow-sm"
            >
              <div className="rounded-lg bg-white p-2 border border-zinc-100">
                <QRCodeSVG
                  data-testid="hero-qr-code"
                  value={qrUrl}
                  size={88}
                  bgColor="#ffffff"
                  fgColor="#002FA7"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-semibold">
                  <Smartphone className="h-3 w-3" />
                  {t("hero.qr_kicker")}
                </div>
                <p className="mt-1.5 font-sans text-sm text-zinc-900 leading-snug">
                  {t("hero.qr_title")}
                </p>
                <p className="mt-1 font-mono text-[10px] text-zinc-500 truncate">
                  {qrUrl.replace(/^https?:\/\//, "")}
                </p>
              </div>
            </div>

            <div className="w-full rounded-2xl border border-zinc-200 bg-white/60 backdrop-blur p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                  {t("hero.preview_label")}
                </span>
                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-[#002FA7]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#002FA7] animate-pulse" />
                  {t("hero.preview_live")}
                </span>
              </div>
              <div className="flex items-center gap-1 h-16">
                {[10, 30, 60, 40, 80, 55, 90, 45, 70, 25, 60, 35, 75, 50, 30].map(
                  (h, i) => (
                    <div
                      key={i}
                      className="wave-bar flex-1 rounded-sm bg-[#002FA7]"
                      style={{
                        height: `${h}%`,
                        animationDelay: `${i * 0.08}s`,
                      }}
                    />
                  )
                )}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs font-mono text-zinc-500">
                <span>{t("hero.preview_time_start")}</span>
                <span>{t("hero.preview_sample")}</span>
                <span>{t("hero.preview_time_end")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div
            data-testid="dropzone-area"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`lg:col-span-7 relative min-h-[320px] rounded-2xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center text-center px-8 py-12 transition-colors duration-300 ${
              dragOver
                ? "border-[#002FA7] bg-[#EFF4FF]"
                : "border-zinc-300 bg-zinc-50/50 hover:bg-[#EFF4FF] hover:border-[#002FA7]/50"
            }`}
          >
            <input
              ref={inputRef}
              data-testid="file-input"
              type="file"
              accept=".pdf,.docx,.txt"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />

            {extracting ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-10 w-10 text-[#002FA7] animate-spin" />
                <p className="font-sans text-sm text-zinc-700">
                  {t("dropzone.extracting")}
                </p>
              </div>
            ) : text ? (
              <div className="w-full text-left" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-[#002FA7]" />
                    <span
                      data-testid="loaded-filename"
                      className="font-mono text-xs uppercase tracking-widest text-zinc-700"
                    >
                      {filename || t("dropzone.pasted")}
                    </span>
                  </div>
                  <button
                    data-testid="clear-text-btn"
                    onClick={clearText}
                    className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors duration-200"
                  >
                    <X className="h-3 w-3" /> {t("dropzone.clear")}
                  </button>
                </div>
                <textarea
                  data-testid="text-area"
                  value={text}
                  onChange={(e) => setText(e.target.value.normalize("NFKC"))}
                  onPaste={(e) => {
                    // Normalize Unicode ligatures (ﬁ, ﬂ, …) from pasted text
                    // so TTS doesn't drop letters. Some PDFs use these
                    // single-codepoint ligatures and copying them keeps the
                    // codepoint intact, which downstream engines silently
                    // ignore. NFKC folds them back to plain ASCII.
                    e.preventDefault();
                    const pasted = e.clipboardData.getData("text").normalize("NFKC");
                    const ta = e.currentTarget;
                    const start = ta.selectionStart ?? text.length;
                    const end = ta.selectionEnd ?? text.length;
                    const next = text.slice(0, start) + pasted + text.slice(end);
                    setText(next);
                    // Restore caret after the inserted content on next tick.
                    requestAnimationFrame(() => {
                      ta.selectionStart = ta.selectionEnd = start + pasted.length;
                    });
                    // Ask the backend to also repair ligature drop-outs
                    // (words missing 'fi'/'fl' because the source clipboard
                    // stripped them). Fires only on paste (not on every
                    // keystroke) to keep it cheap.
                    (async () => {
                      try {
                        const { data } = await axios.post(
                          `${API}/normalize-text`,
                          { text: next },
                          { timeout: 8000 }
                        );
                        if (data?.text && data.text !== next) {
                          setText(data.text);
                          toast.success("Texto normalizado", {
                            description: "Se restauraron palabras con ligaduras faltantes (fi/fl).",
                          });
                        }
                      } catch {
                        /* silent — best-effort UX enhancement */
                      }
                    })();
                  }}
                  className="w-full h-52 resize-none rounded-md border border-zinc-200 bg-white p-3 font-sans text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
                />
                <div className="mt-2 flex items-center justify-between text-xs font-mono text-zinc-500">
                  <span>
                    {t("dropzone.chars_of", {
                      count: text.length.toLocaleString(),
                      max: MAX_CHARS.toLocaleString(),
                    })}
                  </span>
                  <span>
                    {t("dropzone.minutes", {
                      n: Math.max(1, Math.round(text.length / 1000)),
                    })}
                  </span>
                </div>
                <button
                  type="button"
                  data-testid="normalize-btn"
                  onClick={() => normalizeNow(false)}
                  disabled={normalizing || !text.trim()}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-50 transition-colors duration-200"
                >
                  {normalizing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Corregir palabras cortadas (fi / fl)
                </button>
              </div>
            ) : (
              <>
                <div className="relative mb-6">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white border border-zinc-200 shadow-sm">
                    <UploadCloud className="h-7 w-7 text-[#002FA7]" strokeWidth={1.8} />
                  </div>
                </div>
                <p className="font-serif text-2xl text-zinc-950">
                  {t("dropzone.main")}
                </p>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                  {t("dropzone.limits")}
                </p>
                <button
                  data-testid="browse-file-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    inputRef.current?.click();
                  }}
                  className="mt-6 rounded-md border border-zinc-300 bg-white px-5 py-2.5 font-sans text-sm font-medium text-zinc-900 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors duration-200"
                >
                  {t("dropzone.selectFile")}
                </button>
                <div className="mt-4 flex items-center gap-2">
                  <div className="h-px w-8 bg-zinc-300" />
                  <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                    {t("dropzone.or")}
                  </span>
                  <div className="h-px w-8 bg-zinc-300" />
                </div>
                <button
                  data-testid="paste-text-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setText(" ");
                    setFilename(t("dropzone.pasted"));
                  }}
                  className="mt-3 text-sm font-medium text-[#002FA7] hover:underline"
                >
                  {t("dropzone.manual")}
                </button>
              </>
            )}
          </div>

          <div
            data-testid="audio-config-panel"
            className="lg:col-span-5 rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 space-y-6"
          >
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                {t("config.step")}
              </span>
              <h3 className="mt-2 font-serif text-2xl text-zinc-950">
                {t("config.title")}
              </h3>
            </div>

            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                {t("config.voiceLabel")}
              </label>
              <Select value={voice} onValueChange={setVoice}>
                <SelectTrigger
                  data-testid="voice-select"
                  className="mt-2 h-11 rounded-md border-zinc-300 bg-white text-sm font-sans"
                >
                  <SelectValue placeholder={t("config.voicePlaceholder")} />
                </SelectTrigger>
                <SelectContent
                  data-testid="voice-select-content"
                  className="rounded-md border-zinc-200"
                >
                  {myVoices.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="font-mono text-[10px] uppercase tracking-widest text-[#002FA7]">
                        {t("clone.sectionMine")}
                      </SelectLabel>
                      {myVoices.map((v) => (
                        <SelectItem
                          key={v.voice_id}
                          value={v.voice_id}
                          data-testid={`voice-option-mine-${v.voice_id}`}
                          disabled={v.status !== "ready"}
                          className="font-sans text-sm"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Mic className="h-3.5 w-3.5 text-[#002FA7]" />
                            {v.name}
                            {v.status === "ready" ? (
                              <span className="ml-1 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-emerald-700">
                                <Check className="h-2.5 w-2.5" /> {t("clone.ready")}
                              </span>
                            ) : v.status === "failed" ? (
                              <span className="ml-1 font-mono text-[9px] uppercase text-red-600">
                                {t("clone.failed")}
                              </span>
                            ) : (
                              <span className="ml-1 inline-flex items-center gap-1 font-mono text-[9px] uppercase text-amber-700">
                                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                {t("clone.processing")}
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  <SelectGroup>
                    <SelectLabel className="font-mono text-[10px] uppercase tracking-widest text-[#002FA7]">
                      Voces nativas · HeyGen
                    </SelectLabel>
                    {HEYGEN_NATIVE_VOICES.map((v) => (
                      <SelectItem
                        key={v.id}
                        value={v.id}
                        data-testid={`voice-option-heygen-${v.id}`}
                        className="font-sans text-sm"
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className="font-mono text-[10px] rounded bg-[#EFF4FF] px-1.5 py-0.5 text-[#002FA7] font-semibold">
                            {v.region}
                          </span>
                          {v.name}
                          <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">
                            {v.gender === "F" ? "Femenina" : "Masculina"}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {t("clone.sectionStandard")}
                    </SelectLabel>
                    {VOICE_IDS.map((id) => (
                      <SelectItem
                        key={id}
                        value={id}
                        data-testid={`voice-option-${id}`}
                        className="font-sans text-sm"
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className="font-mono text-[10px] rounded bg-zinc-100 px-1.5 py-0.5 text-zinc-600">
                            {VOICE_LANG_TAG[id]}
                          </span>
                          {t(`voices.${id}`)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              <button
                type="button"
                data-testid="open-clone-modal"
                onClick={() => setCloneOpen(true)}
                className="mt-2 w-full text-left rounded-md border border-dashed border-zinc-300 bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors duration-200"
              >
                {t("clone.cloneNew")}
              </button>

              {myVoices.some((v) => v.status === "failed") && (
                <button
                  type="button"
                  data-testid="cleanup-failed-voices"
                  onClick={async () => {
                    const failed = myVoices.filter((v) => v.status === "failed");
                    if (!failed.length) return;
                    try {
                      await Promise.all(
                        failed.map((v) =>
                          axios.delete(`${API}/voices/mine/${v.voice_id}`)
                        )
                      );
                      toast.success(
                        `${failed.length} ${failed.length === 1 ? "voz eliminada" : "voces eliminadas"}`
                      );
                      refreshMyVoices();
                    } catch (err) {
                      toast.error("No se pudo eliminar", {
                        description:
                          err?.response?.data?.detail || err?.message,
                      });
                    }
                  }}
                  className="mt-2 w-full text-left rounded-md border border-red-200 bg-red-50 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-red-700 hover:border-red-400 hover:bg-red-100 transition-colors duration-200"
                >
                  Limpiar voces fallidas (
                  {myVoices.filter((v) => v.status === "failed").length})
                </button>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                  {t("config.speedLabel")}
                </label>
                <span
                  data-testid="speed-value"
                  className="font-mono text-sm font-semibold text-[#002FA7]"
                >
                  {speed.toFixed(2)}x
                </span>
              </div>
              <Slider
                data-testid="speed-slider"
                value={[speed]}
                min={0.5}
                max={2}
                step={0.25}
                onValueChange={(v) => setSpeed(v[0])}
                className="mt-4 [&_.bg-primary]:bg-[#002FA7] [&_.border-primary\\/50]:border-[#002FA7] [&_.bg-primary\\/20]:bg-zinc-200"
              />
              <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                <span>0.5x</span>
                <span>1x</span>
                <span>1.5x</span>
                <span>2x</span>
              </div>
            </div>

            <button
              type="button"
              data-testid="hd-toggle"
              onClick={() => setHdMode((v) => !v)}
              aria-pressed={hdMode}
              className={`w-full flex items-start justify-between gap-3 rounded-md border p-3 text-left transition-colors duration-200 ${
                hdMode
                  ? "border-[#002FA7] bg-[#EFF4FF]"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-800 font-semibold">
                    {t("hd.label")}
                  </span>
                  <span
                    data-testid="hd-badge"
                    className={`font-mono text-[10px] px-1.5 py-0.5 rounded font-bold ${
                      hdMode
                        ? "bg-[#002FA7] text-white"
                        : "bg-zinc-100 text-zinc-500"
                    }`}
                  >
                    {t("hd.badge")}
                  </span>
                </div>
                <p className="mt-1 font-sans text-xs text-zinc-500 leading-snug">
                  {t("hd.hint")}
                </p>
              </div>
              <span
                data-testid="hd-switch"
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 mt-1 ${
                  hdMode ? "bg-[#002FA7]" : "bg-zinc-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${
                    hdMode ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </span>
            </button>

            <button
              data-testid="generate-btn"
              onClick={handleGenerate}
              disabled={generating || extracting}
              className="w-full h-14 rounded-md bg-[#002FA7] text-white font-sans text-base font-semibold flex items-center justify-center gap-2 hover:bg-[#00258A] transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {progress.total > 0
                    ? t("config.generatingProgress", {
                        done: progress.done,
                        total: progress.total,
                      })
                    : t("config.generating")}
                </>
              ) : (
                <>
                  {t("config.generate")} <Headphones className="h-5 w-5" />
                </>
              )}
            </button>
            {generating && progress.total > 0 && (
              <div
                data-testid="generation-progress"
                className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200"
              >
                <div
                  className="h-full bg-[#002FA7] transition-all duration-500"
                  style={{
                    width: `${Math.round((progress.done / progress.total) * 100)}%`,
                  }}
                />
              </div>
            )}
            <QuotaBar refreshKey={quotaRefresh} />
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400">
              {t("config.disclaimer")}
            </p>
          </div>
        </div>
      </div>
      <VoiceCloneModal
        open={cloneOpen}
        onClose={() => setCloneOpen(false)}
        onCloned={() => refreshMyVoices()}
      />
    </section>
  );
}
